// File: src/i18n/LanguageContext.js
/**
 * Shared UI/content language state (en / es / ar).
 *
 * One language selection drives BOTH the app chrome (header, sidebar, search,
 * footer strings from src/i18n/strings.js) and the regulation content toggle
 * in SectionViewer — the header switch and the in-section toggle stay in sync
 * because they read and write this same context.
 *
 * The choice persists via the existing storage helpers (getLanguage /
 * setLanguage), so it survives reloads and matches what SectionViewer used
 * before the toggle moved into navigation. The document's `lang` attribute
 * tracks the selection for accessibility; the shell layout itself stays LTR
 * (regulation text handles its own RTL/bidi rendering).
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getLanguage, setLanguage } from '../utils/storage';
import { FEATURES } from '../utils/constants';
import { translate } from './strings';

const VALID = ['en', 'es', 'ar'];

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }) {
  const enabled = FEATURES.CONTENT_TRANSLATIONS;
  const [lang, setLangState] = useState(() => {
    const saved = enabled ? getLanguage() : 'en';
    return VALID.includes(saved) ? saved : 'en';
  });

  const setLang = useCallback((next) => {
    if (!VALID.includes(next)) return;
    setLangState(next);
    setLanguage(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key, params) => translate(lang, key, params), [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export default LanguageContext;
